/**
 * Filtrowanie i sortowanie listy funkcji presetu.
 *
 * Ta sama lista, ten sam endpoint i ten sam hook żyją w dwóch miejscach: na
 * stronie deweloperskiej i w zakładce „Funkcje" katalogu cen, którą widzi CEO
 * zatwierdzający funkcje. Filtry miała tylko strona deweloperska — czyli akurat
 * nie ta osoba, która musi wyłuskać z listy „Oczekujące". Logika jest tutaj, żeby
 * oba ekrany nie rozjechały się przy kolejnej zmianie.
 */
import type { PricingPresetFunction } from "./types";

export type PresetFunctionSortKey = "status" | "name" | "created_desc" | "created_asc";

/** Oczekujące najpierw — to one wymagają decyzji. */
export const PRESET_FUNCTION_STATUS_ORDER: Record<PricingPresetFunction["status"], number> = {
  pending: 0,
  approved: 1,
  rejected: 2,
};

export const PRESET_FUNCTION_SORT_LABELS: { value: PresetFunctionSortKey; label: string }[] = [
  { value: "status", label: "Sortuj: status (oczekujące najpierw)" },
  { value: "name", label: "Sortuj: nazwa (A–Z)" },
  { value: "created_desc", label: "Sortuj: najnowsze" },
  { value: "created_asc", label: "Sortuj: najstarsze" },
];

export interface PresetFunctionFilters {
  status?: "" | PricingPresetFunction["status"];
  creator?: string;
  search?: string;
  sortKey?: PresetFunctionSortKey;
}

/** Autorzy obecni na liście — do wypełnienia filtra osób. */
export function presetFunctionCreators(
  functions: PricingPresetFunction[],
): [string, string][] {
  const map = new Map<string, string>();
  for (const fn of functions) {
    if (fn.created_by) map.set(fn.created_by, fn.created_by_name ?? "—");
  }
  return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], "pl"));
}

export function filterPresetFunctions(
  functions: PricingPresetFunction[],
  { status = "", creator = "", search = "", sortKey = "status" }: PresetFunctionFilters = {},
): PricingPresetFunction[] {
  const q = search.trim().toLowerCase();
  const rows = functions.filter(
    (fn) =>
      (!status || fn.status === status) &&
      (!creator || fn.created_by === creator) &&
      (!q || fn.name.toLowerCase().includes(q)),
  );
  return [...rows].sort((a, b) => {
    if (sortKey === "name") return a.name.localeCompare(b.name, "pl");
    if (sortKey === "created_desc") return b.created_at.localeCompare(a.created_at);
    if (sortKey === "created_asc") return a.created_at.localeCompare(b.created_at);
    const d =
      PRESET_FUNCTION_STATUS_ORDER[a.status] - PRESET_FUNCTION_STATUS_ORDER[b.status];
    return d !== 0 ? d : a.name.localeCompare(b.name, "pl");
  });
}
